/**
 * Sync Engine
 *
 * Handles background synchronization of offline changes to Salesforce.
 * Processes pending syncs from the route store with retry logic.
 */

import { salesforceClient, isSalesforceRateLimited } from '../salesforce';
import { useRouteStore } from '../../stores/route-store';
import { processMediaQueue, meldQueueStand } from '../media/queue';
import { verwerkWasserijWachtrij } from './wasserij-wachtrij';
import type { AttemptedAction, SkipReason } from '../salesforce/types';

// ============================================================================
// Types
// ============================================================================

interface SyncResult {
  success: boolean;
  syncId: string;
  error?: string;
}

interface SyncEngineConfig {
  maxRetries: number;
  retryDelayMs: number;
  batchSize: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_CONFIG: SyncEngineConfig = {
  maxRetries: 3,
  retryDelayMs: 5000,
  batchSize: 5,
};

// ============================================================================
// Sync Engine Class
// ============================================================================

class SyncEngine {
  private config: SyncEngineConfig;
  private isSyncing = false;
  private syncInterval: ReturnType<typeof setInterval> | null = null;

  constructor(config: Partial<SyncEngineConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Start automatic background sync
   */
  startAutoSync(intervalMs: number = 30000): void {
    if (this.syncInterval) {
      return;
    }

    // Run immediately on start
    this.processQueue();
    void processMediaQueue(); // media-laag: eigen capture-uploads (R2)
    // Telemetrie los van de uploadronde: ook als die ronde hangt of vroeg
    // afbreekt moet de wachtrijstand binnenkomen — juist dán (gethrottled
    // in meldQueueStand zelf, dus dit spamt niet).
    void meldQueueStand();
    void verwerkWasserijWachtrij(); // voorinspectie/reinigen/na-inspectie bij netwerkfout

    // Then run on interval
    this.syncInterval = setInterval(() => {
      this.processQueue();
      void processMediaQueue();
      void meldQueueStand();
      void verwerkWasserijWachtrij();
    }, intervalMs);

    console.log('[SyncEngine] Auto-sync started with interval:', intervalMs);
  }

  /**
   * Stop automatic background sync
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('[SyncEngine] Auto-sync stopped');
    }
  }

  /**
   * Process all pending syncs in the queue
   */
  async processQueue(): Promise<SyncResult[]> {
    if (this.isSyncing) {
      console.log('[SyncEngine] Already syncing, skipping');
      return [];
    }

    // Circuit-breaker: bij een SF-daglimiet niet blijven pushen — dat voedt de
    // retry-storm. De pending acties blijven in de wachtrij voor de volgende ronde.
    if (isSalesforceRateLimited()) {
      console.log('[SyncEngine] SF rate-limited, sync uitgesteld');
      return [];
    }

    const store = useRouteStore.getState();
    const { pendingSyncs } = store;

    if (pendingSyncs.length === 0) {
      return [];
    }

    // Check if online
    if (!navigator.onLine) {
      console.log('[SyncEngine] Offline, skipping sync');
      return [];
    }

    this.isSyncing = true;
    store.setSyncing(true);

    const results: SyncResult[] = [];

    try {
      // Process in batches
      const batch = pendingSyncs.slice(0, this.config.batchSize);

      for (const sync of batch) {
        // Skip if max retries exceeded
        if (sync.retryCount >= this.config.maxRetries) {
          console.warn('[SyncEngine] Max retries exceeded for sync:', sync.id);
          store.removePendingSync(sync.id);
          results.push({
            success: false,
            syncId: sync.id,
            error: 'Max retries exceeded',
          });
          continue;
        }

        try {
          const result = await this.processSyncItem(sync);
          results.push(result);

          if (result.success) {
            store.removePendingSync(sync.id);
          } else {
            store.incrementSyncRetry(sync.id);
          }
        } catch (error) {
          console.error('[SyncEngine] Error processing sync:', sync.id, error);
          store.incrementSyncRetry(sync.id);
          results.push({
            success: false,
            syncId: sync.id,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }

        // Small delay between items to avoid rate limiting
        await this.delay(500);
      }

      // Update last synced if any syncs succeeded
      if (results.some(r => r.success)) {
        store.setLastSynced(new Date().toISOString());
      }
    } finally {
      this.isSyncing = false;
      store.setSyncing(false);
    }

    return results;
  }

  /**
   * Process a single sync item
   */
  private async processSyncItem(sync: {
    id: string;
    type: string;
    ritId: string;
    payload: {
      status: string;
      werkOrderStatus?: string;
      skipReason?: string;
      skipNotes?: string;
      attemptedActions?: AttemptedAction[];
      pickedUpItemIds?: string[];
      notes?: string;
      completedAt: string;
    };
  }): Promise<SyncResult> {
    console.log('[SyncEngine] Processing sync:', sync.type, sync.ritId);

    switch (sync.type) {
      case 'complete_stop':
        return this.syncCompleteStop(sync);

      case 'skip_stop':
        return this.syncSkipStop(sync);

      case 'partial_pickup':
        return this.syncPartialPickup(sync);

      case 'heropen_stop':
        return this.syncHeropenStop(sync);

      default:
        console.warn('[SyncEngine] Unknown sync type:', sync.type);
        return {
          success: false,
          syncId: sync.id,
          error: `Unknown sync type: ${sync.type}`,
        };
    }
  }

  /**
   * Sync a completed stop to Salesforce
   */
  private async syncCompleteStop(sync: {
    id: string;
    ritId: string;
    payload: {
      status: string;
      werkOrderStatus?: string;
      completedAt: string;
    };
  }): Promise<SyncResult> {
    try {
      // Call the API to complete the stop
      await salesforceClient.completeGeplandeRit({
        ritId: sync.ritId,
        completedAt: sync.payload.completedAt,
      });

      console.log('[SyncEngine] Successfully synced stop:', sync.ritId);

      return {
        success: true,
        syncId: sync.id,
      };
    } catch (error) {
      console.error('[SyncEngine] Failed to sync complete stop:', error);

      return {
        success: false,
        syncId: sync.id,
        error: error instanceof Error ? error.message : 'API call failed',
      };
    }
  }

  /**
   * Sync a skipped stop to Salesforce
   */
  private async syncSkipStop(sync: {
    id: string;
    ritId: string;
    payload: {
      status: string;
      skipReason?: string;
      skipNotes?: string;
      attemptedActions?: AttemptedAction[];
      completedAt: string;
    };
  }): Promise<SyncResult> {
    try {
      // Call skip API with reason, attempted actions and notes
      await salesforceClient.skipGeplandeRit({
        ritId: sync.ritId,
        reason: (sync.payload.skipReason || 'Anders') as SkipReason,
        attemptedActions: sync.payload.attemptedActions,
        notes: sync.payload.skipNotes,
      });

      console.log('[SyncEngine] Successfully synced skip:', sync.ritId);

      return {
        success: true,
        syncId: sync.id,
      };
    } catch (error) {
      console.error('[SyncEngine] Failed to sync skip stop:', error);

      return {
        success: false,
        syncId: sync.id,
        error: error instanceof Error ? error.message : 'API call failed',
      };
    }
  }

  /**
   * Heropenen: overgeslagen stop terug naar Ingepland. De server geeft een
   * 200-envelop met success:false als het niet kan (route al afgerond, stop
   * niet overgeslagen); dat is geen retry-geval, dus we loggen en laten de
   * volgende stops-refresh de echte stand terugzetten.
   */
  private async syncHeropenStop(sync: { id: string; ritId: string }): Promise<SyncResult> {
    try {
      const res = await salesforceClient.heropenGeplandeRit(sync.ritId);
      if (!res.success) {
        console.warn('[SyncEngine] Heropenen geweigerd:', sync.ritId, res.message);
        return { success: true, syncId: sync.id };
      }
      console.log('[SyncEngine] Successfully synced heropen:', sync.ritId);
      return { success: true, syncId: sync.id };
    } catch (error) {
      console.error('[SyncEngine] Failed to sync heropen stop:', error);
      return {
        success: false,
        syncId: sync.id,
        error: error instanceof Error ? error.message : 'API call failed',
      };
    }
  }

  /**
   * Sync a partial pickup ("deels opgehaald") to Salesforce
   */
  private async syncPartialPickup(sync: {
    id: string;
    ritId: string;
    payload: {
      status: string;
      pickedUpItemIds?: string[];
      notes?: string;
      reason?: string;
      completedAt: string;
    };
  }): Promise<SyncResult> {
    try {
      await salesforceClient.partialPickupGeplandeRit({
        ritId: sync.ritId,
        pickedUpItemIds: sync.payload.pickedUpItemIds || [],
        notes: sync.payload.notes,
        reason: sync.payload.reason,
        completedAt: sync.payload.completedAt,
      });

      console.log('[SyncEngine] Successfully synced partial pickup:', sync.ritId);

      return {
        success: true,
        syncId: sync.id,
      };
    } catch (error) {
      console.error('[SyncEngine] Failed to sync partial pickup:', error);

      return {
        success: false,
        syncId: sync.id,
        error: error instanceof Error ? error.message : 'API call failed',
      };
    }
  }

  /**
   * Force sync all pending items immediately
   */
  async forceSync(): Promise<SyncResult[]> {
    return this.processQueue();
  }

  /**
   * Check if there are pending syncs
   */
  hasPendingSyncs(): boolean {
    const { pendingSyncs } = useRouteStore.getState();
    return pendingSyncs.length > 0;
  }

  /**
   * Get count of pending syncs
   */
  getPendingSyncCount(): number {
    const { pendingSyncs } = useRouteStore.getState();
    return pendingSyncs.length;
  }

  /**
   * Utility: delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const syncEngine = new SyncEngine();

// ============================================================================
// React Hook for Sync Status
// ============================================================================

import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/database';

export function useSyncStatus() {
  const { pendingSyncs, isSyncing, lastSynced } = useRouteStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // Media-laag: aantal wachtende capture-uploads (live uit Dexie)
  const mediaQueueCount = useLiveQuery(() => db.mediaQueue.count(), [], 0) ?? 0;

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    pendingCount: pendingSyncs.length,
    isSyncing,
    isOnline,
    lastSynced,
    hasPendingSyncs: pendingSyncs.length > 0,
    mediaQueueCount,
  };
}
